import { extend, $, $$, addEvent } from './helper-functions'

export class DynamicSelect {
  constructor (settings, selectors, classes) {
    this.settings = extend({}, settings)

    this.selectors = extend({
      container: '.dynamic-select',
      containerFormSelect: '.dynamic-select--form-select',
      default: '.dynamic-select__default',
      current: '.dynamic-select__current',
      options: '.dynamic-select__options',
      optionsActive: '.dynamic-select__active',
      allOptions: '.dynamic-select__options button',
      option: 'button',
      firstOption: '.dynamic-select__options button:first-child',
      selectField: 'select:not(.form__select--no-replace)'
    }, selectors)

    this.classes = extend({
      container: 'dynamic-select',
      containerFormSelect: 'dynamic-select--form-select',
      options: 'dynamic-select__options',
      optionsActive: 'dynamic-select__active',
      default: 'dynamic-select__default',
      current: 'dynamic-select__current',
      openOptions: 'dynamic-select--open'
    }, classes)
  }

  init () {
    this.replaceSelectFields()
    this.addDefaults()
    this.addEvents()
  }

  replaceSelectFields () {
    const that = this
    const selectFields = $$(that.selectors.selectField)

    selectFields.forEach((select) => {
      const divContainer = document.createElement('div')
      const divOptions = document.createElement('div')

      divContainer.classList.add(that.classes.container, that.classes.containerFormSelect)
      divOptions.classList.add(that.classes.options)
      divContainer.append(divOptions)

      select.after(divContainer)

      const container = select.nextElementSibling
      const options = $(that.selectors.options, container)

      const selectOptions = Array.from(select.options)

      selectOptions.forEach((selectOption, i) => {
        const link = document.createElement('button')
        const text = document.createElement('span')

        if (i === select.selectedIndex) {
          const divDefault = document.createElement('div')
          divDefault.classList.add(that.classes.default)
          divDefault.textContent = selectOption.text
          container.prepend(divDefault)
          link.classList.add(that.classes.optionsActive)
        }

        if (selectOption.value) {
          link.dataset.value = selectOption.value
        }
        text.textContent = selectOption.text
        link.append(text)

        options.append(link)
      })

      // hide select
      select.style.display = 'none'
      select.setAttribute('tabindex', '-1')
    })
  }

  addDefaults () {
    const that = this
    const containers = $$(that.selectors.container)

    containers.forEach((container) => {
      const defaultElement = $(that.selectors.default, container)
      let currentText = ''

      if (defaultElement) {
        currentText = defaultElement.textContent
      } else {
        const firstOption = $(that.selectors.firstOption, container)
        if (firstOption) {
          currentText = firstOption.textContent
        }
      }

      const button = document.createElement('button')
      button.classList.add(that.classes.current)
      button.textContent = currentText

      container
        .prepend(button)
    })
  }

  addEvents () {
    const that = this
    const currentButtons = $$(that.selectors.current)
    const allOptions = $$(that.selectors.allOptions)

    currentButtons.forEach((currentButton) => {
      const select = currentButton.closest(that.selectors.container).previousElementSibling

      currentButton.addEventListener('keydown', (event) => {
        if (!select.hasAttribute('readonly')) {
          const container = currentButton.closest(that.selectors.container)
          const options = $(that.selectors.options, container)
          const focusedOption = $(that.selectors.optionsActive, options) || options.children[0]

          if (event.key === ' ' || event.key === 'Enter') {
            if (container.classList.contains(that.classes.openOptions)) {
              focusedOption.dispatchEvent(new Event('click'))
              currentButton.focus()
            } else {
              container.classList.add(that.classes.openOptions)
            }
            return false
          } else if (event.key === 'ArrowDown') {
            if (container.classList.contains(that.classes.openOptions)) {
              const nextOption = focusedOption.nextElementSibling

              if (nextOption) {
                focusedOption.classList.remove(that.classes.optionsActive)
                nextOption.classList.add(that.classes.optionsActive)
              }
            } else {
              container.classList.add(that.classes.openOptions)
            }
            return false
          } else if (event.key === 'ArrowUp') {
            if (container.classList.contains(that.classes.openOptions)) {
              const previousOption = focusedOption.previousElementSibling

              if (previousOption) {
                focusedOption.classList.remove(that.classes.optionsActive)
                previousOption.classList.add(that.classes.optionsActive)
              }
            } else {
              container.classList.add(that.classes.openOptions)
            }
            return false
          } else if (event.key === 'Escape' || event.key === 'Tab') {
            if (container.classList.contains(that.classes.openOptions)) {
              container.classList.remove(that.classes.openOptions)
              currentButton.focus()
            }
          }
        }
      })

      currentButton.addEventListener('click', (event) => {
        event.preventDefault()

        if (!select.hasAttribute('readonly')) {
          const currentElement = event.target
          const currentContainer = currentElement.closest(that.selectors.container)

          if (currentContainer.classList.contains(that.classes.openOptions)) {
            currentContainer.classList.remove(that.classes.openOptions)

            return false
          }

          currentButtons.forEach((currentButton) => {
            const currentContainer = currentButton.closest(that.selectors.container)
            if (currentContainer) {
              currentContainer.classList.remove(that.classes.openOptions)
            }
          })

          currentElement.focus()
          currentElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter' }))
        }
      })
    })

    allOptions.forEach((allOption) => {
      addEvent(allOption, 'click', (event) => {
        const option = event.target.closest(that.selectors.option)
        const container = option.closest(that.selectors.container)
        const current = $(that.selectors.current, container)
        const select = container.previousElementSibling
        const allOptions = $$(that.selectors.allOptions, container)

        event.preventDefault()

        if (select) {
          allOptions.forEach((allOption) => {
            allOption.classList.remove(that.classes.optionsActive)
          })

          select.value = option.dataset.value
          select.dispatchEvent(new Event('change', {
            bubbles: true
          }))

          current.textContent = option.textContent

          option.classList.add(that.classes.optionsActive)
          container.classList.remove(that.classes.openOptions)
        }
      })
    })

    document.querySelector('body').addEventListener('click', (event) => {
      if (!event.target.closest(that.selectors.container)) {
        const containers = $$(that.selectors.container)
        containers.forEach((container) => {
          container.classList.remove(that.classes.openOptions)
        })
      }
    })
  }
}

export default DynamicSelect
